import { ContainerTag, FileInfo } from "../../../api/admin/models_admin";

/**
 * サブカテゴリ設定画面で「編集を完了」を押すまで実行を保留しておく変更。
 *
 * ファイルタグのPUTごとにインデクサを走らせると、AI Searchのインデクサは同時実行できず
 * 409になるうえ、実行中はこの画面自体がロックされて編集を続けられない。
 * そのため変更はすべてここに畳んでおき、「定義の反映 → ファイルの反映 → インデクサ実行」の
 * 順にまとめて実行する。
 */

/** サブカテゴリ定義(コンテナタグ)の変更内容。表示用の内訳と、PUTする最終形を持つ */
export interface TagDefinitionChange {
    // PUT /api/tags/container/<seq> に渡す最終形 (キー -> 名前)
    tags: { [key: string]: string }
    added: string[]
    renamed: { from: string, to: string }[]
    removed: string[]
}

/** ファイルへのサブカテゴリ付与の変更内容 */
export interface TagFileChange {
    file: FileInfo
    tagKeys: string[]
}

/** タグキー配列を順序無視で比較する */
export const isSameTagKeys = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    const setB = new Set(b);
    return a.every(k => setB.has(k));
};

/** 編集中のサブカテゴリ定義に存在しないキーを落とす。削除されたサブカテゴリの残骸を除くために使う */
export const normalizeTagKeys = (keys: string[], availableKeys: Set<string>): string[] =>
    keys.filter(k => availableKeys.has(k));

/** 名前の前後の空白は入力の揺れなので、比較・送信の前に落とす */
export const trimTags = (tags: ContainerTag[]): ContainerTag[] =>
    tags.map(t => ({ key: t.key, value: t.value.trim() }));

/**
 * サブカテゴリ定義の入力を検証する。問題があればメッセージを返す。
 * 「登録」ボタンを廃止して一括実行に寄せたため、実行前にまとめて確認する
 */
export const validateTags = (tags: ContainerTag[]): string | null => {
    const trimmed = trimTags(tags);
    if (trimmed.some(t => t.value === '')) {
        return 'サブカテゴリの名前を入力してください';
    }
    const values = trimmed.map(t => t.value);
    if (new Set(values).size !== values.length) {
        return 'サブカテゴリ名が重複しています';
    }
    return null;
};

/** サブカテゴリ定義の差分。変更が無ければnull */
export const buildTagDefinitionChange = (
    original: ContainerTag[],
    edited: ContainerTag[]
): TagDefinitionChange | null => {
    const trimmed = trimTags(edited);
    const originalByKey = new Map(original.map(t => [t.key, t.value]));
    const editedByKey = new Map(trimmed.map(t => [t.key, t.value]));

    const added: string[] = [];
    const renamed: { from: string, to: string }[] = [];
    for (const tag of trimmed) {
        const before = originalByKey.get(tag.key);
        if (before === undefined) {
            added.push(tag.value);
        } else if (before !== tag.value) {
            renamed.push({ from: before, to: tag.value });
        }
    }

    const removed = original.filter(t => !editedByKey.has(t.key)).map(t => t.value);

    if (added.length === 0 && renamed.length === 0 && removed.length === 0) return null;

    const tags: { [key: string]: string } = {};
    trimmed.forEach(t => { tags[t.key] = t.value; });
    return { tags, added, renamed, removed };
};

/**
 * ファイルへのサブカテゴリ付与の差分。
 *
 * 削除されたサブカテゴリのキーは編集前後の双方から落として比較する。
 * 削除に伴うファイル側の除去は PUT /api/tags/container/<seq> が全件に対して行うため、
 * ここで対象に含めるとユーザーが触っていないファイルまでPUTすることになる。
 */
export const buildTagFileChanges = (
    files: FileInfo[],
    fileTagKeys: Record<number, string[]>,
    originalFileTagKeys: Record<number, string[]>,
    editedTags: ContainerTag[]
): TagFileChange[] => {
    const availableKeys = new Set(editedTags.map(t => t.key));
    const changes: TagFileChange[] = [];
    for (const file of files) {
        const current = normalizeTagKeys(fileTagKeys[file.seq] ?? [], availableKeys);
        const original = normalizeTagKeys(originalFileTagKeys[file.seq] ?? [], availableKeys);
        if (!isSameTagKeys(current, original)) {
            changes.push({ file, tagKeys: current });
        }
    }
    return changes;
};
